import {
  defaultAward,
  defaultCertification,
  defaultCustomSection,
  defaultInterest,
  defaultProject,
  defaultPublication,
  defaultReference,
  defaultVolunteer,
} from "@reactive-resume/schema";

import type { AdditionalBuilderStep } from "@/client/constants/builder-steps";

export type FieldDefinition = {
  name: string;
  label: string;
  placeholder: string;
  kind?: "text" | "month" | "period" | "textarea" | "url" | "keywords";
  required?: boolean;
  full?: boolean;
  suggestions?: string[];
};

export type SectionDefinition = {
  title: string;
  subtitle: string;
  singular: string;
  empty: string;
  defaultValue: Record<string, any>;
  fields: FieldDefinition[];
  itemTitle: (item: any) => string;
  itemDescription: (item: any) => string;
};

export const SECTION_DEFINITIONS: Record<AdditionalBuilderStep, SectionDefinition> = {
  projects: {
    title: "Vos projets",
    subtitle: "Présentez les réalisations qui démontrent concrètement votre savoir-faire.",
    singular: "projet",
    empty: "Aucun projet ajouté",
    defaultValue: defaultProject,
    fields: [
      {
        name: "name",
        label: "Nom du projet",
        placeholder: "Ex. Application de gestion",
        required: true,
      },
      {
        name: "description",
        label: "Votre rôle",
        placeholder: "Ex. Développeur frontend",
        suggestions: [
          "Développeur frontend",
          "Développeur backend",
          "Développeur full stack",
          "UX/UI Designer",
          "Chef de projet",
          "Data Analyst",
        ],
      },
      {
        name: "date",
        label: "Période du projet",
        placeholder: "",
        kind: "period",
        full: true,
      },
      {
        name: "url",
        label: "Lien du projet",
        placeholder: "https://…",
        kind: "url",
      },
      {
        name: "keywords",
        label: "Technologies ou compétences",
        placeholder: "Ajoutez une compétence puis Entrée",
        kind: "keywords",
        full: true,
        suggestions: ["React", "TypeScript", "Node.js", "Python", "Figma", "Gestion de projet"],
      },
      {
        name: "summary",
        label: "Description et résultats",
        placeholder: "Expliquez le contexte, vos actions et les résultats obtenus…",
        kind: "textarea",
        full: true,
      },
    ],
    itemTitle: (item) => item.name,
    itemDescription: (item) => item.description || item.date,
  },
  certifications: {
    title: "Vos certifications",
    subtitle: "Ajoutez les certifications qui renforcent la crédibilité de votre profil.",
    singular: "certification",
    empty: "Aucune certification ajoutée",
    defaultValue: defaultCertification,
    fields: [
      {
        name: "name",
        label: "Nom de la certification",
        placeholder: "Ex. AWS Certified",
        required: true,
      },
      {
        name: "issuer",
        label: "Organisme délivrant",
        placeholder: "Ex. Amazon Web Services",
        suggestions: ["Amazon Web Services", "Google", "Microsoft", "Cisco", "Coursera", "HubSpot"],
      },
      {
        name: "date",
        label: "Date d’obtention",
        placeholder: "",
        kind: "month",
      },
      {
        name: "url",
        label: "Lien de vérification",
        placeholder: "https://…",
        kind: "url",
      },
      {
        name: "summary",
        label: "Précisions",
        placeholder: "Ajoutez une précision utile…",
        kind: "textarea",
        full: true,
      },
    ],
    itemTitle: (item) => item.name,
    itemDescription: (item) => [item.issuer, item.date].filter(Boolean).join(" · "),
  },
  volunteer: {
    title: "Vos engagements bénévoles",
    subtitle: "Valorisez vos responsabilités, vos actions et leur impact.",
    singular: "expérience bénévole",
    empty: "Aucun engagement ajouté",
    defaultValue: defaultVolunteer,
    fields: [
      {
        name: "organization",
        label: "Organisation",
        placeholder: "Ex. Croix-Rouge",
        required: true,
      },
      {
        name: "position",
        label: "Rôle",
        placeholder: "Ex. Coordinateur bénévole",
        suggestions: [
          "Bénévole",
          "Coordinateur bénévole",
          "Responsable d’équipe",
          "Mentor",
          "Formateur",
        ],
      },
      { name: "location", label: "Lieu", placeholder: "Ex. Douala, Cameroun" },
      {
        name: "date",
        label: "Période",
        placeholder: "",
        kind: "period",
        full: true,
      },
      {
        name: "url",
        label: "Site de l’organisation",
        placeholder: "https://…",
        kind: "url",
        full: true,
      },
      {
        name: "summary",
        label: "Missions et impact",
        placeholder: "Décrivez vos principales contributions…",
        kind: "textarea",
        full: true,
      },
    ],
    itemTitle: (item) => item.organization,
    itemDescription: (item) => [item.position, item.location].filter(Boolean).join(" · "),
  },
  awards: {
    title: "Vos récompenses",
    subtitle: "Mettez en avant les distinctions qui témoignent de vos réussites.",
    singular: "récompense",
    empty: "Aucune récompense ajoutée",
    defaultValue: defaultAward,
    fields: [
      {
        name: "title",
        label: "Titre de la récompense",
        placeholder: "Ex. Prix du meilleur projet",
        required: true,
      },
      { name: "awarder", label: "Organisme", placeholder: "Ex. UNICEF" },
      { name: "date", label: "Date", placeholder: "", kind: "month" },
      { name: "url", label: "Lien", placeholder: "https://…", kind: "url" },
      {
        name: "summary",
        label: "Contexte",
        placeholder: "Expliquez brièvement cette distinction…",
        kind: "textarea",
        full: true,
      },
    ],
    itemTitle: (item) => item.title,
    itemDescription: (item) => [item.awarder, item.date].filter(Boolean).join(" · "),
  },
  interests: {
    title: "Vos centres d’intérêt",
    subtitle: "Ajoutez quelques intérêts pertinents qui révèlent votre personnalité.",
    singular: "centre d’intérêt",
    empty: "Aucun centre d’intérêt ajouté",
    defaultValue: defaultInterest,
    fields: [
      {
        name: "name",
        label: "Centre d’intérêt",
        placeholder: "Ex. Photographie",
        required: true,
        full: true,
        suggestions: [
          "Photographie",
          "Lecture",
          "Sport",
          "Voyage",
          "Musique",
          "Bénévolat",
          "Technologie",
        ],
      },
      {
        name: "keywords",
        label: "Précisions",
        placeholder: "Ajoutez une précision puis Entrée",
        kind: "keywords",
        full: true,
      },
    ],
    itemTitle: (item) => item.name,
    itemDescription: (item) => item.keywords?.join(" · ") ?? "",
  },
  publications: {
    title: "Vos publications",
    subtitle: "Présentez vos articles, recherches, ouvrages ou contenus spécialisés.",
    singular: "publication",
    empty: "Aucune publication ajoutée",
    defaultValue: defaultPublication,
    fields: [
      {
        name: "name",
        label: "Titre",
        placeholder: "Ex. Concevoir une interface accessible",
        required: true,
      },
      {
        name: "publisher",
        label: "Éditeur ou média",
        placeholder: "Ex. Medium",
        suggestions: ["Medium", "LinkedIn", "ResearchGate", "GitHub", "Presse spécialisée"],
      },
      {
        name: "date",
        label: "Date de publication",
        placeholder: "",
        kind: "month",
      },
      {
        name: "url",
        label: "Lien vers la publication",
        placeholder: "https://…",
        kind: "url",
      },
      {
        name: "summary",
        label: "Résumé",
        placeholder: "Présentez le sujet et votre contribution…",
        kind: "textarea",
        full: true,
      },
    ],
    itemTitle: (item) => item.name,
    itemDescription: (item) => [item.publisher, item.date].filter(Boolean).join(" · "),
  },
  references: {
    title: "Vos références",
    subtitle: "Ajoutez les personnes capables de recommander votre travail.",
    singular: "référence",
    empty: "Aucune référence ajoutée",
    defaultValue: defaultReference,
    fields: [
      {
        name: "name",
        label: "Nom complet",
        placeholder: "Ex. Marie Dupont",
        required: true,
      },
      {
        name: "description",
        label: "Fonction et relation",
        placeholder: "Ex. Responsable de projet",
      },
      {
        name: "url",
        label: "Profil ou moyen de contact",
        placeholder: "https://…",
        kind: "url",
        full: true,
      },
      {
        name: "summary",
        label: "Recommandation",
        placeholder: "Ajoutez un court témoignage ou une précision…",
        kind: "textarea",
        full: true,
      },
    ],
    itemTitle: (item) => item.name,
    itemDescription: (item) => item.description,
  },
};

export const CUSTOM_SECTION_DEFINITION: SectionDefinition = {
  title: "Votre section personnalisée",
  subtitle: "Ajoutez les informations qui complètent votre parcours.",
  singular: "élément",
  empty: "Aucun élément ajouté",
  defaultValue: defaultCustomSection,
  fields: [
    {
      name: "name",
      label: "Titre",
      placeholder: "Ex. Mission",
      required: true,
    },
    {
      name: "description",
      label: "Sous-titre",
      placeholder: "Ex. Rôle ou organisme",
    },
    {
      name: "date",
      label: "Période",
      placeholder: "",
      kind: "period",
      full: true,
    },
    { name: "location", label: "Lieu", placeholder: "Ex. Yaoundé" },
    {
      name: "url",
      label: "Lien",
      placeholder: "https://…",
      kind: "url",
      full: true,
    },
    {
      name: "keywords",
      label: "Mots-clés",
      placeholder: "Compétence 1, Compétence 2",
      kind: "keywords",
      full: true,
    },
    {
      name: "summary",
      label: "Description",
      placeholder: "Décrivez cet élément…",
      kind: "textarea",
      full: true,
    },
  ],
  itemTitle: (item) => item.name,
  itemDescription: (item) => item.description || item.date,
};
